import { getFcmToken, updateFcmToken } from '@features/auth';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

declare global {
    interface WindowEventMap {
        fcmToken: CustomEvent<string>;
    }
    interface Window {
        // Ponts natifs possibles. Seul AndroidInterface est confirme : les autres
        // couvrent les conventions usuelles cote iOS, ou le nom du canal n'est pas
        // connu (projet Xcode indisponible).
        iOSInterface?: { getFcmToken?: () => string | null };
        IOSInterface?: { getFcmToken?: () => string | null };
        NativeInterface?: { getFcmToken?: () => string | null };
        webkit?: { messageHandlers?: Record<string, { postMessage: (message: unknown) => void }> };
        fcmToken?: string | null;
        __FCM_TOKEN__?: string | null;
        // Point d'entree que le shell natif peut appeler via evaluateJavaScript.
        setFcmToken?: (token: string) => void;
    }
}

/**
 * Sources de token connues ou plausibles, essayees dans l'ordre.
 *
 * Android expose `AndroidInterface` (addJavascriptInterface). iOS utilise une
 * WKWebView, ou ce pont n'existe pas : on tente les conventions habituelles en
 * attendant de connaitre le contrat reel du shell.
 */
const TOKEN_SOURCES: Array<{ name: string; read: () => unknown }> = [
    { name: 'AndroidInterface.getFcmToken', read: () => window.AndroidInterface?.getFcmToken?.() },
    { name: 'iOSInterface.getFcmToken', read: () => window.iOSInterface?.getFcmToken?.() },
    { name: 'IOSInterface.getFcmToken', read: () => window.IOSInterface?.getFcmToken?.() },
    { name: 'NativeInterface.getFcmToken', read: () => window.NativeInterface?.getFcmToken?.() },
    { name: 'window.fcmToken', read: () => window.fcmToken },
    { name: 'window.__FCM_TOKEN__', read: () => window.__FCM_TOKEN__ }
];

/**
 * Capture a l'import du module, donc avant que le hook n'installe la sienne :
 * permet de distinguer une globale fournie par le shell natif de la notre.
 */
const SET_FCM_TOKEN_PREEXISTED = typeof window.setFcmToken === 'function';

/**
 * Marqueur de version du rapport de diagnostic.
 *
 * Sans lui, impossible de distinguer « le correctif ne fonctionne pas » de
 * « le telephone execute un bundle en cache ». index.html est servi sans
 * Cache-Control, un ancien bundle peut donc etre resservi longtemps.
 * A incrementer a chaque modification de ce fichier.
 */
const PROBE_VERSION = 'b40e62f';

/**
 * Lit le token directement depuis le pont natif, sans passer par le store.
 *
 * Le store n'est pas fiable au moment de la connexion : l'application n'utilise
 * pas de PersistGate, si bien que l'action REHYDRATE de redux-persist arrive
 * apres le premier rendu et remplace le slice `authentication` en entier —
 * effacant au passage le token que ce hook venait d'y poser. Le pont natif, lui,
 * detient la bonne valeur des que le shell l'a injectee.
 */
export const readNativeFcmToken = (): string | null => readToken();

const readToken = (): string | null => {
    for (const source of TOKEN_SOURCES) {
        try {
            const value = source.read();

            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        } catch {
            // Un pont absent ou qui leve ne doit pas interrompre les suivants.
        }
    }

    return null;
};

/**
 * TEMPORAIRE — Decrit ce que le shell natif expose reellement.
 *
 * Le projet Xcode n'etant pas accessible, c'est la webapp qui doit rapporter le
 * contrat disponible. Le resultat part avec la connexion et est journalise
 * cote API par LoginProbe. A retirer une fois le pont iOS identifie.
 */
export const describeNativeBridges = (): string => {
    const found: string[] = ['v=' + PROBE_VERSION];

    try {
        // Distingue Safari d'une WKWebView : Safari expose un jeton Version/ dans
        // son UA, pas la webview. `standalone` signale une PWA installee.
        found.push('webkit=' + (window.webkit ? 'oui' : 'non'));
        found.push('standalone=' + String((window.navigator as Navigator & { standalone?: boolean }).standalone ?? 'n/a'));

        // Les canaux WKWebView : c'est l'information decisive cote iOS.
        const handlers = window.webkit?.messageHandlers;

        found.push('messageHandlers=' + (handlers ? '[' + Object.keys(handlers).join(',') + ']' : 'absent'));

        // Notre propre point d'entree ne compte pas : seule sa preexistence
        // indiquerait que le shell natif en fournit un.
        found.push('setFcmToken.preexistant=' + (SET_FCM_TOKEN_PREEXISTED ? 'oui' : 'non'));

        for (const source of TOKEN_SOURCES) {
            try {
                const value = source.read();

                if (value !== undefined && value !== null) {
                    found.push(source.name + '=' + (typeof value === 'string' ? 'len:' + value.length : typeof value));
                }
            } catch {
                found.push(source.name + '=throws');
            }
        }

        // Tout objet global au nom evocateur, pour reperer un pont non anticipe.
        const suspects = Object.keys(window).filter(
            (key) => key !== 'setFcmToken' && /interface|native|bridge|fcm|token|firebase|push/i.test(key)
        );

        if (suspects.length > 0) {
            found.push('globals=[' + suspects.slice(0, 20).join(',') + ']');
        }
    } catch (error) {
        found.push('error=' + String(error));
    }

    return found.length > 0 ? found.join(' | ') : 'aucun pont natif detecte';
};

export const useNativeFCMToken = () => {
    const dispatch = useDispatch();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const fcmTokenFromStore = useSelector(getFcmToken);

    useEffect(() => {
        const publish = (token: string | null | undefined): boolean => {
            if (typeof token !== 'string' || token.length === 0) {
                return false;
            }

            dispatch(updateFcmToken(token));

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            return true;
        };

        // Mode « push » : le shell natif peut nous transmettre le token quand il
        // l'obtient, sans que la webapp ait a le reclamer. C'est le seul mode
        // possible depuis une WKWebView, ou la communication est unidirectionnelle.
        window.setFcmToken = (token: string) => {
            publish(token);
        };

        const onTokenEvent = (event: CustomEvent<string>) => {
            publish(event.detail);
        };

        window.addEventListener('fcmToken', onTokenEvent);

        // Mode « pull » : interrogation directe des ponts disponibles.
        const hasToken = publish(readToken());

        if (!hasToken && !fcmTokenFromStore) {
            intervalRef.current = setInterval(() => {
                publish(readToken());
            }, 3000);
        }

        return () => {
            window.removeEventListener('fcmToken', onTokenEvent);
            delete window.setFcmToken;

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [dispatch, fcmTokenFromStore]);
};
